import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MagnifyingGlass, MapPin, X } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import ListState from '../components/ListState';
import CategoryBadges from '../components/CategoryBadges';
import { AGE_CATEGORIES, GENDER_CATEGORIES } from '../lib/tournamentCategories';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFilters = { city: '', format: '', q: '', ages: [], genders: [] };

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    let cancelled = false;
    const fetchTournaments = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.city) params.append('city', filters.city);
        if (filters.format) params.append('format', filters.format);
        if (filters.q) params.append('q', filters.q);
        if (filters.ages.length) params.append('age', filters.ages.join(','));
        if (filters.genders.length) params.append('gender', filters.genders.join(','));
        params.append('status', 'approved');
        const { data } = await axios.get(`${API}/tournaments?${params.toString()}`);
        if (!cancelled) setTournaments(data);
      } catch (e) {
        logError('Failed to fetch tournaments:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTournaments();
    return () => { cancelled = true; };
  }, [filters]);

  const handleFilter = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
  const toggleAge = (a) => setFilters((f) => ({ ...f, ages: f.ages.includes(a) ? f.ages.filter((x) => x !== a) : [...f.ages, a] }));
  const toggleGender = (g) => setFilters((f) => ({ ...f, genders: f.genders.includes(g) ? f.genders.filter((x) => x !== g) : [...f.genders, g] }));
  const clearFilters = () => setFilters(initialFilters);
  const hasFilters = Boolean(filters.city || filters.format || filters.q || filters.ages.length || filters.genders.length);

  return (
    <>
      <SEO
        title="Browse Basketball Tournaments in India"
        description="Discover and register for basketball tournaments across India. Filter by city, format (1v1, 3v3, 5v5), age, and gender. Find your next game on Beyond Ball India."
        path="/tournaments"
      />

      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 animate-fade-up">
            <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-2">Discover</div>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-3" data-testid="tournaments-title">
              Basketball tournaments in India
            </h1>
            <p className="text-gray-600 text-base sm:text-lg">All formats. All cities. All in one place.</p>
          </div>

          {/* Search */}
          <div className="relative mb-4 animate-fade-up delay-100">
            <MagnifyingGlass size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              name="q"
              placeholder="Search tournaments by name..."
              value={filters.q}
              onChange={handleFilter}
              className="w-full bg-white border border-gray-200 focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-full text-gray-900 pl-12 pr-4 py-3.5 text-sm outline-none transition-all"
              data-testid="search-input"
            />
          </div>

          {/* Top-level filters */}
          <div className="flex flex-wrap gap-2 mb-3 animate-fade-up delay-200">
            <input
              type="text"
              name="city"
              placeholder="City"
              value={filters.city}
              onChange={handleFilter}
              className="bg-white border border-gray-200 focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-full text-gray-900 px-4 py-2 text-sm outline-none transition-all"
              data-testid="filter-city-input"
            />
            <select
              name="format"
              value={filters.format}
              onChange={handleFilter}
              className="bg-white border border-gray-200 focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-full text-gray-900 px-4 py-2 text-sm outline-none transition-all"
              data-testid="filter-format-select"
            >
              <option value="">All formats</option>
              <option value="1v1">1v1</option>
              <option value="3v3">3v3</option>
              <option value="5v5">5v5</option>
            </select>
            {hasFilters && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 px-4 py-2 text-sm font-medium" data-testid="clear-filters-btn">
                <X size={14} weight="bold" /> Clear
              </button>
            )}
          </div>

          {/* Age + Gender filter pills */}
          <div className="mb-6 space-y-3 animate-fade-up delay-200">
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Age category</div>
              <div className="flex flex-wrap gap-2" data-testid="age-filter-group">
                {AGE_CATEGORIES.map((a) => {
                  const active = filters.ages.includes(a);
                  return (
                    <button
                      key={a}
                      onClick={() => toggleAge(a)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        active ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`age-filter-${a}`}
                      aria-pressed={active}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Gender</div>
              <div className="flex flex-wrap gap-2" data-testid="gender-filter-group">
                {GENDER_CATEGORIES.map((g) => {
                  const active = filters.genders.includes(g);
                  return (
                    <button
                      key={g}
                      onClick={() => toggleGender(g)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`gender-filter-${g}`}
                      aria-pressed={active}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Results */}
          <ListState
            loading={loading}
            empty={tournaments.length === 0}
            skeleton={
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="aspect-[16/10] skeleton"></div>
                    <div className="p-5 space-y-3">
                      <div className="h-5 skeleton rounded w-3/4"></div>
                      <div className="h-3 skeleton rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            }
            emptyState={
              <div className="text-center py-20" data-testid="no-tournaments-message">
                <div className="text-6xl mb-4">🏀</div>
                <p className="text-gray-900 font-semibold mb-2">No tournaments found</p>
                <p className="text-gray-500 text-sm">Try adjusting your filters or check back soon.</p>
              </div>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tournaments.map((t, i) => (
                <Link
                  key={t.tournament_id}
                  to={`/tournaments/${t.tournament_id}`}
                  className="card-lift bg-white rounded-2xl border border-gray-200 overflow-hidden animate-fade-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                  data-testid={`tournament-card-${i}`}
                >
                  <div className="relative aspect-[16/10] bg-gradient-to-br from-orange-100 to-orange-50 overflow-hidden">
                    {t.banner_url ? (
                      <img
                        src={t.banner_url}
                        alt={t.name}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `${t.banner_focus_x ?? 50}% ${t.banner_focus_y ?? 50}%` }}
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-6xl font-bold text-orange-200">{t.format}</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-semibold text-gray-900">
                      {t.format}
                    </div>
                    {t.computed_status === 'ended' && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-gray-900/90 text-white text-[10px] font-bold uppercase tracking-wider">
                        Tournament Ended
                      </div>
                    )}
                    {t.computed_status === 'registration_closed' && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-red-600/90 text-white text-[10px] font-bold uppercase tracking-wider">
                        Registration Closed
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-1">{t.name}</h3>
                    <CategoryBadges ages={t.age_categories || []} genders={t.gender_categories || []} className="mb-3" />
                    <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
                      <MapPin size={14} weight="bold" />
                      <span>{t.city}, {t.state}</span>
                    </div>
                    <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase font-semibold">Dates</div>
                        <div className="text-xs font-semibold text-gray-900">{t.dates}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-gray-400 uppercase font-semibold">Deadline</div>
                        <div className="text-xs font-semibold text-[#F97316]">{t.registration_deadline}</div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </ListState>
        </div>
      </div>
    </>
  );
};

export default Tournaments;
